import { RegisterComponent } from '@/common/register-component';
import { NylasSchedulerConfigConnector } from '@/connector/nylas-scheduler-config-connector';
import { debug } from '@/utils/utils';
import { AttachInternals, Component, Element, Event, EventEmitter, Host, Listen, Prop, State, Watch, h } from '@stencil/core';
import { NylasSchedulerEditor } from '../nylas-scheduler-editor/nylas-scheduler-editor';
import { DEFAULT_FORM_FIELD_TYPES, FIELD_TYPES } from '@/common/constants';
import { Configuration, AdditionalFields, ThemeConfig } from '@nylas/core';
import Sortable from 'sortablejs';
import i18next from '@/utils/i18n';
import { LANGUAGE_CODE } from '@/common/constants';

interface AdditionalFieldsInternal extends AdditionalFields {
  isOpen: boolean;
  readonly?: boolean;
  typeLabel: string;
  key?: string; // Make optional
  error?: string; // Make optional
  fieldLabelKey?: string; // Make optional
}
const fieldTypesWithOptions = ['dropdown', 'radio_button'];
const fieldTypesWithMultipleOptions = ['multi_select'];

/**
 * The `nylas-booking-form-config` component is a form input for adding additional fields to the booking form.
 * @part nbfc__header - The header of the booking form
 * @part nbfc__add_field - The add field selection container
 * @part nbfc__add_field-button - The add field  selection button
 * @part nbfc__add_field-content - The add field  selection dropdown content
 * @part nbfc__additional_fields - The aditional fields container
 * @part nbfc__form-field - The single form container
 * @part nbfc__form-field-header - The single form header
 * @part nbfc__form-field-content - The single form content
 */
@Component({
  tag: 'nylas-booking-form-config',
  styleUrl: 'nylas-booking-form-config.scss',
  shadow: true,
  formAssociated: true,
})
export class NylasBookingFormConfig {
  /**
   * The element <nylas-booking-form-config> itself.
   */
  @Element() host!: HTMLNylasBookingFormConfigElement;

  /**
   * @internal
   * The selected configuration.
   */
  @Prop() selectedConfiguration?: Configuration;

  /**
   * The name of the booking form config.
   */
  @Prop() name: string = 'additional-fields';

  /**
   * The additional fields to be displayed on the booking form.
   */
  @Prop() additionalFields?: AdditionalFields[];

  /**
   * The selected language.
   */
  @Prop() selectedLanguage?: LANGUAGE_CODE;

  /**
   * @standalone
   * The theme configuration.
   */
  @Prop({ attribute: 'theme-config' }) readonly themeConfig?: ThemeConfig;

  /**
   * This event is fired when the selected availability / open hours change.
   */
  @Event() valueChanged!: EventEmitter<{
    value: string;
    name: string;
  }>;

  /**
   * The element internals.
   */
  @AttachInternals() internals!: ElementInternals;

  @State() staticFields = [
    {
      type: 'text',
      label: 'Your name',
      key: 'your_name',
      typeLabel: 'Short text',
      readonly: true,
      required: true,
      isOpen: false,
      fieldLabelKey: 'your_name',
      order: 0,
    },
    {
      type: 'email',
      label: 'Your Email',
      key: 'your_email',
      typeLabel: 'Email',
      readonly: true,
      isOpen: false,
      required: true,
      fieldLabelKey: 'your_email',
      order: 1,
    },
  ];

  @State() formFields!: AdditionalFieldsInternal[];

  @State() formFieldsOrder!: AdditionalFieldsInternal[];

  @State() count: number = 0;

  @State() watchFields!: {
    title: string;
    description: string;
  };
  @State() fieldTypes = DEFAULT_FORM_FIELD_TYPES.map(f => {
    return { label: i18next.t(`nylasBookingFormConfig.fieldType.${f.value}`), value: f.value };
  });

  /**
   * When a name prop is passed, stencil does not automatically set the name attribute on the host element.
   * Since this component is form-associated, the name attribute is required for form submission.
   * This is a workaround to ensure that the name attribute is set on the host element.
   */
  @Watch('name')
  elementNameChangedHandler(newValue: string) {
    debug('nylas-calendar-picker', 'elementNameChangedHandler', newValue);
    this.host.setAttribute('name', newValue);
  }

  @Watch('selectedConfiguration')
  selectedConfigurationChangedHandler(newValue: Configuration) {
    this.updateFormFields(newValue, false);
  }

  @Watch('selectedLanguage')
  selectedLanguageChangedHandler(newVal: LANGUAGE_CODE, oldVal: LANGUAGE_CODE) {
    if (newVal === oldVal) return;
    i18next.changeLanguage(newVal);
    this.fieldTypes = DEFAULT_FORM_FIELD_TYPES.map(f => {
      return { label: i18next.t(`nylasBookingFormConfig.fieldType.${f.value}`), value: f.value };
    });
  }

  @Watch('themeConfig')
  themeConfigChangedHandler(newVal: ThemeConfig, oldVal: ThemeConfig) {
    if (newVal === oldVal) return;
    this.applyThemeConfig(newVal);
  }

  connectedCallback() {
    debug('nylas-booking-form-config', 'connectedCallback');
    // Use queueMicrotask to defer theme application until after React has set props.
    // This fixes the timing issue where connectedCallback fires before React sets themeConfig.
    queueMicrotask(() => {
      if (this.themeConfig) {
        this.applyThemeConfig(this.themeConfig);
        this.host.setAttribute('data-themed', '');
      }
    });
  }

  disconnectedCallback() {
    debug('nylas-booking-form-config', 'disconnectedCallback');
  }

  componentWillLoad() {
    debug('nylas-booking-form-config', 'componentWillLoad');
    this.host.setAttribute('name', this.name);
    this.formFields = [];
    this.formFieldsChangedHandler(this.formFields, false);
  }

  componentDidLoad() {
    debug('nylas-booking-form-config', 'componentDidLoad');
    if (this.selectedConfiguration) {
      this.updateFormFields(this.selectedConfiguration, false);
    }
  }

  /**
   * This method is essentially a workaround to check if the internals are available because
   * the unit tests in stencil do not support the internals.
   * @returns boolean
   */
  get isInternalsAvailable() {
    return this.internals !== undefined && typeof this.internals.setFormValue === 'function';
  }

  updateFormFields(config: Configuration, valueChanged: boolean = true) {
    this.watchFields = {
      title: config?.event_booking?.title || '',
      description: config?.event_booking?.description || '',
    };
    const additionalFields =
      Object.entries(config?.scheduler?.additional_fields || {}).map(([id, field]) => ({ ...(field as AdditionalFields), key: id })) || this.additionalFields || [];

    this.formFields = [
      ...additionalFields
        .sort((a, b) => a.order - b.order)
        .map((f: AdditionalFields, _i) => {
          const fieldType = FIELD_TYPES.find(field => field.type === f.type);
          if (!fieldType) {
            return { ...f, isOpen: false, typeLabel: 'Unknown' };
          }
          const typeLabel = fieldType.label;
          const order = f.order || 0;
          return { ...f, order: order - 1, isOpen: false, typeLabel, fieldLabelKey: f.type };
        }),
    ];
    this.formFields = [...this.formFields];

    this.formFieldsChangedHandler(this.formFields, valueChanged);
    const container = this.host.shadowRoot?.getElementById('fields');
    Sortable.create(container, {
      animation: 150,
      swap: true, // Enable swap plugin
      swapClass: 'highlight', // The class applied to the hovered swap item
      onEnd: this.swapFields.bind(this),
      handle: '.form-field_header', // Only allow dragging by the header
      filter: '.fixed', // Disable dragging for elements with the 'fixed' class
      fallbackTolerance: 3, // So that we can select items on mobile
      onMove: evt => !evt.related.classList.contains('fixed'), // Prevent moving if target has 'fixed' class
    });
  }

  formFieldsChangedHandler(newValue: AdditionalFieldsInternal[], valueChanged: boolean = true) {
    const additionalFields = newValue || [];
    // Remove readonly fields, sort by order
    const sortedFields = additionalFields
      .filter(f => !f.readonly)
      .sort((a, b) => a.order - b.order)
      .map((f, i) => {
        const field: AdditionalFields = {
          type: f.type,
          required: f.required,
          label: f.label,
          order: i + 1,
          options: f.options,
          key: f.key,
          default: f.default,
          fieldLabelKey: f.fieldLabelKey,
        };
        return { ...field };
      });

    // Map fields to object
    const addFieldsMap = sortedFields.reduce((acc, field) => {
      if (field.key) {
        // Only add fields with defined keys
        acc[field.key] = {
          type: field.type,
          required: field.required,
          order: field.order,
          options: field.options,
          label: field.label,
          default: field.default,
        };
      }
      return acc;
    }, {});
    this.isInternalsAvailable && this.internals.setFormValue(JSON.stringify(addFieldsMap), this.name);

    if (valueChanged) {
      // Do not emit the valueChanged event on initial load
      this.valueChanged.emit({
        value: JSON.stringify(addFieldsMap),
        name: this.name,
      });
    }
    this.formFields = [...this.formFields];
  }

  /**
   * This function finds the next missing number in an array of strings (keys of additional_fields).
   * We use this function to generate the next consecutive index number for the key.
   * For example,
   * - If the keys are ['dropdown_another-one_1', 'dropdown_Whatever-choose-one_3'] the function will return 2.
   * - If the array is empty, the function will return 1.
   * - If there are no missing numbers in between, the function will return the next number after the last element.
   *
   * @param arr of keys stored in the formFields for the given field type
   * @returns a number that is the next (consecutive) missing number in the array
   */
  findNextMissingNumber(arr: string[]): number {
    if (arr.length === 0) {
      return 1; // If the array is empty, the first number is 1
    }

    // Extract numbers from strings by splitting on underscore and popping the last element
    const numbers = arr
      .map(item => {
        const parts = item.split('_'); // Split the string by '_'
        const lastPart = parts.pop(); // Get the last part (should be the number)
        const parsedNumber = parseInt(lastPart || '', 10);
        return isNaN(parsedNumber) ? null : parsedNumber; // Return the parsed number or null if not a valid number
      })
      .filter((num): num is number => num !== null) // Filter out any null values and assert that the result is a number
      .sort((a, b) => a - b); // Sort numbers in ascending order

    // Iterate through the sorted numbers to find the missing number
    for (let i = 0; i < numbers.length - 1; i++) {
      if (numbers[i + 1] !== numbers[i] + 1) {
        return numbers[i] + 1; // Return the missing number
      }
    }

    // If no number is missing in between, return the next number after the last element
    return numbers.length > 0 ? numbers[numbers.length - 1] + 1 : 1;
  }

  @Listen('nylasFormDropdownChanged')
  nylasFormDropdownChangedHandler(
    event: CustomEvent<{
      value: string;
      name: string;
    }>,
  ) {
    const { name, value } = event.detail;
    if (name === 'add-field') {
      const field = FIELD_TYPES.find(f => f.type === value);
      const maxOrder = Math.max(...this.formFields.map(f => f.order));
      const existingFieldKeys = this.formFields.filter(f => f.type === field.type && f.key !== undefined).map(f => f.key as string);
      const next = this.findNextMissingNumber(existingFieldKeys);
      const newField = { ...field, label: field.label, key: `${field.type}_${field.label.split(' ').join('-')}_${next}`, order: maxOrder + 1 }; // Copy object + Ensure unique order
      this.formFields = [...this.formFields, newField];
      this.formFieldsChangedHandler(this.formFields);
    } else if (name.includes('-default-value')) {
      const fieldIndex = parseInt(name.split('-')[0] || '0');
      let defaultValue = value;
      if (value === 'null') {
        defaultValue = '';
      }
      this.fieldDefaultValueChanged(fieldIndex, { detail: { value: defaultValue } });
    }
  }

  @Listen('selectedOptionsChanged')
  selectedOptionsChangedHandler(event: CustomEvent<{ value: string[]; name: string }>) {
    const { name, value } = event.detail;
    if (name.includes('-default-value')) {
      const fieldIndex = parseInt(name.split('-')[0] || '0');
      const defaultValue = value.join(', ');
      this.fieldDefaultValueChanged(fieldIndex, { detail: { value: defaultValue } });
    }
  }

  @Listen('nylasFormInputChanged')
  nylasFormInputChangedHandler(event: CustomEvent<{ value: string; name: string }>) {
    const { name } = event.detail;
    if (name.startsWith('default-value-')) {
      const fieldIndex = parseInt(name.split('-').pop() || '0');
      this.fieldDefaultValueChanged(fieldIndex, event);
    } else {
      const [fieldIndex, optionIndex] = name.split('_');
      if (optionIndex) {
        this.fieldOptionChange(parseInt(fieldIndex), parseInt(optionIndex), event);
      } else {
        this.fieldLabelChange(parseInt(fieldIndex), event);
      }
    }
  }

  @Listen('valueChanged', { target: 'document' })
  valueChangedHandler(event: CustomEvent<{ value: string; name: string }>) {
    const { name, value } = event.detail;
    if (name === 'title' || name === 'description') {
      this.watchFields = {
        ...this.watchFields,
        [name]: value,
      };
    }
  }

  swapFields(event) {
    const { oldIndex, newIndex } = event;
    if (oldIndex == null || newIndex == null || oldIndex === newIndex) {
      return;
    }
    // Create a copy of the current fields array
    const updatedFields = [...this.formFields];

    // Remove the dragged item from its old position
    const [draggedItem] = updatedFields.splice(oldIndex, 1);

    // Insert the dragged item at its new position
    updatedFields.splice(newIndex, 0, draggedItem);

    // Recalculate order for each field (starting at 1 as required)
    updatedFields.forEach((field, index) => {
      field.order = index + 1;
    });

    // Update state and trigger UI re-render
    this.formFields = updatedFields;
    this.formFieldsChangedHandler(this.formFields);
  }

  fieldToggle(index) {
    this.formFields = this.formFields.map((f, i) => {
      if (index == i) {
        f.isOpen = !f.isOpen;
      }
      return f;
    });
    this.formFieldsChangedHandler(this.formFields);
  }
  staticFieldToggle(index) {
    this.staticFields = this.staticFields.map((f, i) => {
      if (index == i) {
        f.isOpen = !f.isOpen;
      }
      return f;
    });
    this.staticFields = [...this.staticFields];
  }
  fieldRemove(index) {
    this.formFields = this.formFields.filter((_, i) => i !== index);
    this.formFieldsChangedHandler(this.formFields);
  }

  fieldRequired(index) {
    this.formFields = this.formFields.map((field, i) => {
      if (i === index) {
        field.required = !field.required;
      }
      return field;
    });
    this.formFieldsChangedHandler(this.formFields);
  }
  fieldLabelChange(ind, event) {
    const target = event.detail;
    this.formFields = this.formFields.map((field, i) => {
      if (i === ind) {
        const label = target.value;
        return {
          ...field,
          label: label,
        };
      }
      return field;
    });
    this.formFieldsChangedHandler(this.formFields);
  }
  fieldOptionAdd(index) {
    this.formFields = this.formFields.map((field, i) => {
      if (i === index) {
        if (!field.options) {
          field.options = [];
        }
        field.options = [...field.options, ''];
      }
      return field;
    });
    this.formFieldsChangedHandler(this.formFields);
  }

  fieldOptionRemove(fieldIndex, index) {
    this.formFields = this.formFields.map((field, i) => {
      if (i === fieldIndex) {
        if (!field.options) {
          field.options = [];
        }
        field.options = field.options.filter((_, i) => i !== index);
      }
      return field;
    });
    this.formFieldsChangedHandler(this.formFields);
  }
  fieldOptionChange(fieldIndex, index, event) {
    this.formFields = this.formFields.map((field, i) => {
      if (i === fieldIndex) {
        if (!field.options) {
          field.options = [];
        }
        field.options = field.options.map((o, i) => {
          if (i === index) {
            o = event.detail.value;
          }
          return o;
        });
      }
      return field;
    });
    this.formFieldsChangedHandler(this.formFields);
  }

  fieldDefaultValueChanged(index, event) {
    this.formFields = this.formFields.map((field, i) => {
      if (i === index) {
        field.default = event.detail.value;
      }
      return field;
    });
    this.formFieldsChangedHandler(this.formFields);
  }

  applyThemeConfig(themeConfig?: ThemeConfig) {
    if (themeConfig) {
      for (const [key, value] of Object.entries(themeConfig)) {
        this.host.style.setProperty(`${key}`, value);
      }
    }
  }

  @RegisterComponent<NylasBookingFormConfig, NylasSchedulerConfigConnector, Exclude<NylasSchedulerEditor['stores'], undefined>>({
    name: 'nylas-booking-form-config',
    stateToProps: new Map([
      ['schedulerConfig.selectedConfiguration', 'selectedConfiguration'],
      ['schedulerConfig.selectedLanguage', 'selectedLanguage'],
      ['schedulerConfig.themeConfig', 'themeConfig'],
    ]),
    fireRegisterEvent: true,
  })
  render() {
    return (
      <Host>
        <div class="nylas-booking-form-config" part="nbfc">
          <div class="header" part="nbfc__header">
            <div class="header_text">
              <h3>{i18next.t('nylasBookingFormConfig.headerTitle')}</h3>
              <p>{i18next.t('nylasBookingFormConfig.headerSubTitle')}</p>
            </div>
            <div class="header_action">
              <select-dropdown
                name="add-field"
                exportparts="sd_dropdown: nbfc__add_field, sd_dropdown-button: nbfc__add_field-button, sd_dropdown-content: nbfc__add_field-dropdown-content"
                options={this.fieldTypes}
                withSearch={false}
                withChevron={false}
                dropdownButtonText={i18next.t('nylasBookingFormConfig.dropdownButtonText')}
                themeConfig={this.themeConfig}
              >
                <span slot="select-icon">
                  <plus-icon width="15" height="15"></plus-icon>
                </span>
              </select-dropdown>
            </div>
          </div>
          <div class="content">
            <div class="additional_fields static_fields" part="nbfc__static_fields">
              {this.staticFields.map((field, i) => {
                return (
                  <div class={`form-field draggable ${field.readonly && 'fixed'}`} part="nbfc__form_field" key={i}>
                    <div class="form-field_header" part="nbfc__form_field-header">
                      <span class="dragable">
                        <dragable-icon width="24" height="25"></dragable-icon>
                      </span>
                      <div class="form-field_header_text">
                        <h4>{i18next.t(`nylasBookingFormConfig.fieldLabel.${field.fieldLabelKey}`)}</h4>
                        <p>{i18next.t(`nylasBookingFormConfig.fieldType.${field.type}`)}</p>
                      </div>
                      <div class="form-field_header_actions">
                        <span class={`is-required`}>{field.required ? i18next.t('required') : i18next.t('optional')}</span>
                        <span
                          class={`chevron ${field.isOpen ? 'open' : 'closed'}`}
                          onClick={() => {
                            this.staticFieldToggle(i);
                          }}
                        >
                          <chevron-icon width="24" height="24" />
                        </span>
                      </div>
                    </div>
                    <div class={`form-field_content ${!field.isOpen && 'hidden'}`} part="nbfc__form_field-content">
                      <div class="inputs">
                        <input-component
                          class={'label-input'}
                          name={`${i}`}
                          key={i}
                          label={i18next.t('nylasBookingFormConfig.label')}
                          required={false}
                          readOnly={field.readonly}
                          defaultValue={field.label}
                          themeConfig={this.themeConfig}
                        >
                          <div class="required-input" slot="additional-input">
                            <input
                              type="checkbox"
                              name={`required_${field.order}`}
                              id={`required_${field.order}`}
                              onClick={() => {
                                this.fieldRequired(i);
                              }}
                              checked={field.required}
                              disabled={field.readonly}
                            />

                            <label htmlFor={`required_${field.order}`} aria-label="Required">
                              {i18next.t('required')}
                            </label>
                          </div>
                        </input-component>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div id="fields" class="additional_fields" part="nbfc__additional_fields">
              {this.formFields?.map((field, i) => {
                return (
                  <div class={`form-field draggable ${field?.readonly ? 'fixed' : ''}`} part="nbfc__form_field" key={field.key}>
                    <div class="form-field_header" part="nbfc__form_field-header">
                      <span class="dragable">
                        <dragable-icon width="24" height="25"></dragable-icon>
                      </span>
                      <div class="form-field_header_text">
                        <h4>{field.label || i18next.t(`nylasBookingFormConfig.fieldLabel.${field.fieldLabelKey}`)}</h4>
                        <p>{i18next.t(`nylasBookingFormConfig.fieldType.${field.type}`)}</p>
                      </div>
                      <div class="form-field_header_actions">
                        <span class={`is-required`}>{field.required ? i18next.t('required') : i18next.t('optional')}</span>
                        {!field.readonly && (
                          <button
                            onClick={() => {
                              const title = this.watchFields.title;
                              const description = this.watchFields.description;
                              if (title && field.key && title.includes(field.key)) {
                                field.error = i18next.t('nylasBookingFormConfig.fieldInTitleError');
                                this.formFields = [...this.formFields];
                                this.formFieldsChangedHandler(this.formFields);
                                setTimeout(() => {
                                  field.error = '';
                                  this.formFields = [...this.formFields];
                                  this.formFieldsChangedHandler(this.formFields);
                                }, 3000);
                                return;
                              }
                              if (description && field.key && description.includes(field.key)) {
                                field.error = i18next.t('nylasBookingFormConfig.fieldInDescriptionError');
                                this.formFields = [...this.formFields];
                                this.formFieldsChangedHandler(this.formFields);
                                setTimeout(() => {
                                  field.error = '';
                                  this.formFields = [...this.formFields];
                                  this.formFieldsChangedHandler(this.formFields);
                                }, 3000);
                                return;
                              }
                              this.fieldRemove(i);
                            }}
                          >
                            <close-icon />
                          </button>
                        )}
                        <span
                          class={`chevron ${field.isOpen ? 'open' : 'closed'}`}
                          onClick={() => {
                            this.fieldToggle(i);
                          }}
                        >
                          <chevron-icon width="24" height="24" />
                        </span>
                      </div>
                    </div>
                    <span
                      class={{
                        'error': true,
                        'fade-out': !field?.error || field.error === '',
                      }}
                    >
                      {field.error}
                    </span>
                    <div class={`form-field_content ${!field.isOpen && 'hidden'}`} part="nbfc__form_field-content">
                      <div class="inputs">
                        <input-component
                          class={'label-input'}
                          name={`${i}`}
                          key={`${i}-${field.key}-label`}
                          label={i18next.t('nylasBookingFormConfig.label')}
                          required={false}
                          readOnly={field.readonly}
                          defaultValue={field.label}
                          themeConfig={this.themeConfig}
                        >
                          <div class="required-input" slot="additional-input">
                            <input
                              type="checkbox"
                              name={`required_${i}`}
                              id={`required_${i}`}
                              onClick={() => {
                                this.fieldRequired(i);
                              }}
                              checked={field.required}
                              disabled={field.readonly}
                            />

                            <label htmlFor={`required_${i}`} aria-label="Required">
                              {i18next.t('required')}
                            </label>
                          </div>
                        </input-component>
                        {!field.readonly && fieldTypesWithOptions.includes(field.type) && (
                          <select-dropdown
                            name={`${i}-${field.key}-default-value`}
                            key={`${i}-${field.key}-default-value`}
                            label={i18next.t('nylasBookingFormConfig.defaultValue')}
                            required={false}
                            readOnly={field.readonly}
                            options={[
                              { value: 'null', label: i18next.t('nylasBookingCalendarPicker.selectOptionLabel') },
                              ...(field.options?.map(o => ({ value: o, label: o })) || []),
                            ]}
                            emptyValue={i18next.t('nylasBookingCalendarPicker.selectOptionLabel')}
                            withSearch={false}
                            themeConfig={this.themeConfig}
                          />
                        )}
                        {!field.readonly && field.type === 'checkbox' && (
                          <select-dropdown
                            name={`${i}-${field.key}-default-value`}
                            key={`${i}-${field.key}-default-value`}
                            label={i18next.t('nylasBookingFormConfig.defaultValue')}
                            required={false}
                            readOnly={field.readonly}
                            options={[{ value: 'null', label: i18next.t('nylasBookingCalendarPicker.selectOptionLabel') }, ...['true', 'false'].map(o => ({ value: o, label: o }))]}
                            emptyValue={i18next.t('nylasBookingCalendarPicker.selectOptionLabel')}
                            withSearch={false}
                            themeConfig={this.themeConfig}
                          />
                        )}
                        {!field.readonly && fieldTypesWithMultipleOptions.includes(field.type) && (
                          <multi-select-dropdown
                            name={`${i}-${field.key}-default-value`}
                            key={`${i}-${field.key}-default-value`}
                            label={i18next.t('nylasBookingFormConfig.defaultValue')}
                            required={false}
                            selectAtLeastOneOptionLabel={i18next.t('selectOneOrMoreOptions')}
                            multipleOptionsSelectedLabel={i18next.t('multipleOptionsSelected')}
                            readOnly={field.readonly}
                            options={field.options?.filter(o => !!o && o !== null).map(o => ({ value: o, label: o })) || []}
                            themeConfig={this.themeConfig}
                          />
                        )}
                        {!field.readonly &&
                          field.type !== 'date' &&
                          field.type !== 'checkbox' &&
                          !fieldTypesWithOptions.includes(field.type) &&
                          !fieldTypesWithMultipleOptions.includes(field.type) && (
                            <input-component
                              class={'default-value'}
                              name={`default-value-${i}`}
                              key={`${i}-${field.key}-default-value`}
                              label={i18next.t('nylasBookingFormConfig.defaultValue')}
                              required={false}
                              readOnly={field.readonly}
                              defaultValue={field.default}
                              themeConfig={this.themeConfig}
                            />
                          )}
                        {!field.readonly && field.type === 'date' && (
                          <nylas-date-component
                            class={'default-value'}
                            name={`default-value-${i}`}
                            key={`${i}-${field.key}-default-value`}
                            label={i18next.t('nylasBookingFormConfig.defaultValue')}
                            required={false}
                            readOnly={field.readonly}
                            defaultValue={field.default}
                            themeConfig={this.themeConfig}
                          />
                        )}
                      </div>
                      {field.options != undefined && (
                        <div class="options-container">
                          <h4>{i18next.t('nylasBookingFormConfig.allOptionsHeader')}</h4>
                          <div class="options">
                            {field.options.map((o, j) => {
                              return (
                                <div class="option">
                                  <input-component
                                    key={j}
                                    name={`${i}_${j}`}
                                    label={field.typeLabel + ' option ' + (j + 1)}
                                    required={true}
                                    defaultValue={o}
                                    themeConfig={this.themeConfig}
                                  >
                                    {j > 1 && (
                                      <div class="required-input" slot="additional-input">
                                        <button
                                          onClick={() => {
                                            this.fieldOptionRemove(i, j);
                                          }}
                                        >
                                          <close-icon />
                                        </button>
                                      </div>
                                    )}
                                  </input-component>
                                </div>
                              );
                            })}
                          </div>
                          <button
                            onClick={() => {
                              this.fieldOptionAdd(i);
                            }}
                            part="nap__add-time-range"
                          >
                            <add-circle-icon /> {i18next.t('nylasBookingFormConfig.addOption')}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </Host>
    );
  }
}
